import { withTranslation } from "react-i18next";
import WithRouter from "../../../../components/Common/WithRouter";
import React, { useEffect, useRef, useState } from "react";
import StateLessGrid from "../../../../components/Shared/StateLessGrid";
import QuickCopyDropdown from "../../../../components/Shared/QuickCopyDropdown";
import { Button } from "primereact/button";
import { Col, Row } from "react-bootstrap";
import { Field, Form, Formik, FormikProps } from "formik";
import { InputField } from "../../../../components/Shared/InputField";
import { includes, isArray, isEmpty } from "lodash";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Tooltip from "../../../../components/Common/Display/Tooltip";
import { utcDateToLocalDate } from "../../../../utils/Helper";
import { ButtonGroup } from "primereact/buttongroup";
import AlpAvisoOutboundGroupDetailPage from "./AlpAvisoOutboundGroupDetailPage";
import HasAccess from "../../../../utils/HasAccess";
import { request } from "../../../../utils/Request";
import { confirmPopup, ConfirmPopup } from "primereact/confirmpopup";
import AlpOutboundLoadGroupAccept from "../../loads/partials/AlpOutboundLoadGroup/AlpOutboundLoadGroupAccept";
import AlpOutboundLoadGroupReset from "../../loads/partials/AlpOutboundLoadGroup/AlpOutboundLoadGroupReset";
import AlpOutboundLoadGroupInTransit from "../../loads/partials/AlpOutboundLoadGroup/AlpOutboundLoadGroupInTransit";
import AlpOutboundLoadGroupUpdate from "../../loads/partials/AlpOutboundLoadGroup/AlpOutboundLoadGroupUpdate";
import AlpOutboundLoadGroupUnloadRequest from "../../loads/partials/AlpOutboundLoadGroup/AlpOutboundLoadGroupUnloadRequest";
import AlpOutboundLoadGroupDetach from "../../loads/partials/AlpOutboundLoadGroup/AlpOutboundLoadGroupDetach";
import { AlpOutboundLoadGroupStatusChips, GateOutStatusChip } from "../../loads/partials/LoadStatusChip";
import { navigateBackToList } from "../../../../utils/listNavigation";
import LoadComments from "../../loads/partials/LoadComments";

const AlpAvisoOutboundGroupList = (props: any) => {
    const { t, moduleState, toastify } = props;
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [operation, setOperation] = useState<any>(searchParams.get('operation') ?? null);
    const [operationId, setOperationId] = useState<any>(searchParams.get('operationId') ?? null);
    const [initSearchFormState] = useState<any>({ search: null });
    const [total, setTotal] = useState(0);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const tableRef: any = useRef();
    const [dropdowns, setDropdowns] = useState<any>({ drivers: [], transportVehicles: [] });

    // Action panels (copied from LoadList behaviour)
    const [acceptAlpOutboundLoadGroupId, setAcceptAlpOutboundLoadGroupId] = useState<any>(null);
    const [resetAlpOutboundLoadGroupId, setResetAlpOutboundLoadGroupId] = useState<any>(null);
    const [inTransitAlpOutboundLoadGroupId, setInTransitAlpOutboundLoadGroupId] = useState<any>(null);
    const [editAlpOutboundLoadGroupId, setEditAlpOutboundLoadGroupId] = useState<any>(null);
    const [unloadRequestAlpOutboundLoadGroupId, setUnloadRequestAlpOutboundLoadGroupId] = useState<any>(null);
    const [detachAlpOutboundLoadGroupId, setDetachAlpOutboundLoadGroupId] = useState<any>(null);
    const [commentLoad, setCommentLoad] = useState<any>(null);

    const onAcceptAlpOutboundLoadGroup = (id: any) => setAcceptAlpOutboundLoadGroupId(id);
    const onResetAlpOutboundLoadGroup = (id: any) => setResetAlpOutboundLoadGroupId(id);
    const onInTransitAlpOutboundLoadGroup = (id: any) => setInTransitAlpOutboundLoadGroupId(id);
    const onEditAlpOutboundLoadGroup = (id: any) => setEditAlpOutboundLoadGroupId(id);
    const onUnloadRequestAlpOutboundLoadGroup = (id: any) => setUnloadRequestAlpOutboundLoadGroupId(id);


    const refreshTable = () => {
        if ( tableRef.current ) {
            tableRef.current.onRefresh();
        }
    };

    const onOpenLoadComments = (row: any) => {
        if ( !row?.group_token ) {
            return;
        }

        setCommentLoad({
            type: "AlpOutboundLoadGroup",
            load_source_id: row.group_token,
            load_number: Array.isArray(row.load_numbers) && row.load_numbers.length
                ? row.load_numbers.join(", ")
                : row.group_token,
        });
    };

    const onDeleteAlpOutboundLoadGroup = async (groupToken: string) => {
        try {
            const detailResponse = await request({
                url: `/suppliers/provider/aviso/outbound/groups/${ groupToken }`,
            });
            const detailData = detailResponse?.data?.data ?? detailResponse?.data ?? {};
            const loads = Array.isArray(detailData?.alp_outbound_loads) ? detailData.alp_outbound_loads : [];
            const eligibleLoadIds = loads
                .map((load: any) => load?.id)
                .filter((id: any) => !isEmpty(id));

            if ( eligibleLoadIds.length <= 0 ) {
                toastify && toastify(t("action not allowed: 404"), "error");
                return;
            }

            await request({
                method: 'POST',
                url: `/suppliers/alp/aviso/outbound/loads/${ groupToken }/detach`,
                data: {
                    load_ids: eligibleLoadIds,
                    allow_empty_group: true,
                },
            });

            toastify && toastify(t("record deleted", { item: t('outbound load') }), "info");
            refreshTable();
        } catch (e: any) {
            if ( e.status === 422 ) {
                toastify && toastify(t("unprocessable entity"), "error");
            } else if ( e.status === 404 ) {
                toastify && toastify(t("action not allowed: 404"), "error");
            } else {
                toastify && toastify(t("server error"), "error");
            }
        }
    };

    const getDropdowns = async () => {
        try {
            const response = await request({
                url: '/suppliers/loads/create',
                params: { type: 'list' },
            });
            const data = response?.data?.data ?? response?.data;
            setDropdowns({
                drivers: data?.drivers ?? [],
                transportVehicles: data?.transportVehicles ?? [],
            });
        } catch (e: any) {
            setDropdowns({ drivers: [], transportVehicles: [] });
        }
    };

    useEffect(() => {
        getDropdowns();
    }, []);

    const onOperationClick = (operationToBePerformed: any = 'detail', id: any = null) => {
        const sp = new URLSearchParams(location.search);
        sp.set('operation', operationToBePerformed);
        sp.set('operationId', id);

        if ( isEmpty(operationToBePerformed) ) {
            sp.delete('operation');
        }

        if ( isEmpty(id) ) {
            sp.delete('operationId');
        }

        navigate({
            pathname: location.pathname,
            search: sp.toString(),
        }, { replace: true });

        setOperationId(id);
        setOperation(operationToBePerformed);
    };

    const onCloseOperation = (reset: any = true) => {
        // If this page was opened from another module (eg. LoadList),
        // respect back_to like other ALP detail modules.
        if ( !isEmpty(searchParams.get('back_to')) ) {
            navigateBackToList(navigate, `${ searchParams.get('back_to') }`);
            return;
        }

        onOperationClick(null, null);
        onReset(reset);
        focusOnSearch();
    };

    const onReset = (reset: any = true) => {
        const mStates = { ...moduleState };

        if ( reset ) {
            mStates.page = 1;
            mStates.search = null;
        }

        props.setQuery(mStates);
    };

    const onSearch = ({ search }: any) => {
        props.setQuery({
            ...moduleState,
            search: search
        });
    };

    const focusOnSearch = () => {
        const searchInput: HTMLInputElement | null = document.querySelector('input[name="search"]');
        if ( searchInput ) {
            searchInput.focus();
        }
    };

    const AlpOutboundLoadGroupActionButton = (actionProps: any) => {
        const _row: any = actionProps.row;
        const isMixed = _row.status === 'mixed';
        const groupToken = _row.group_token;
        const canInTransit = Number(_row.in_transit_eligible_count || 0) > 0;
        const canUnload = Number(_row.unload_eligible_count || 0) > 0;
        const canDestroy = Number(_row.destroy_eligible_count || 0) > 0;

        return (
            <>
                <HasAccess hasAnyAccess={ ['alpoutboundloadsupdate'] }>
                    {
                        isMixed &&
                        <Tooltip tooltip={ t('mixed') }>
                            <span className={ "ms-1 text-red-500" }>
                                <i className="fas fa-exclamation-triangle" />
                            </span>
                        </Tooltip>
                    }

                    {
                        Number(_row.qty || 0) > 1 &&
                        <Tooltip tooltip={ t('detach from group') }>
                            <a
                                onClick={ () => setDetachAlpOutboundLoadGroupId(groupToken) }
                                className={ "cursor-pointer ms-1 text-orange-500" }
                            >
                                <i className="fas fa-unlink" />
                            </a>
                        </Tooltip>
                    }

                    {
                        !isMixed && includes(['accepted'], _row.status) && isEmpty(_row.reset_at) && isEmpty(_row.gate_out_confirmed_at)
                        && /*@ts-ignore*/
                        <Tooltip tooltip={ t('reset') }>
                            <a onClick={ () => onResetAlpOutboundLoadGroup(groupToken) }
                               className={ "cursor-pointer ms-1 text-primary-500" }>
                                <i className="fas fa-redo-alt" />
                            </a>
                        </Tooltip>
                    }

                    {
                        !isMixed && includes(['announced'], _row.status)
                        &&
                        /*@ts-ignore*/
                        <Tooltip tooltip={ t('accept') }>
                            <a onClick={ () => onAcceptAlpOutboundLoadGroup(groupToken) }
                               className={ "cursor-pointer ms-1 text-blue-500" }>
                                <i className="fas fa-check" />
                            </a>
                        </Tooltip>
                    }

                    {
                        !isMixed && includes(['accepted', 'announced'], _row.status)
                        && ( isEmpty(_row.reset_at) )
                        &&
                        /*@ts-ignore*/
                        <Tooltip tooltip={ t('edit item') }>
                            <a onClick={ () => onEditAlpOutboundLoadGroup(groupToken) }
                               className={ "cursor-pointer ms-1 text-yellow-500" }>
                                <i className="fas fa-edit" />
                            </a>
                        </Tooltip>
                    }

                    {
                        Number(_row.qty || 0) > 0 &&
                        <HasAccess hasAnyAccess={ ['alpgrouploadsdestroy'] }>
                            <Tooltip tooltip={ t('delete item') }>
                                <a
                                    title={ t('delete item') }
                                    className={ "cursor-pointer ms-1 text-red-500" }
                                    onClick={ (event: any) => confirmPopup({
                                        target: event.currentTarget,
                                        message: t('do you want to delete this record?'),
                                        icon: 'pi pi-info-circle',
                                        // @ts-ignore
                                        defaultFocus: 'reject',
                                        acceptClassName: 'p-button-danger',
                                        accept: () => onDeleteAlpOutboundLoadGroup(groupToken),
                                        reject: () => {
                                        },
                                    }) }
                                >
                                    <i className="fas fa-trash-alt" />
                                </a>
                            </Tooltip>
                        </HasAccess>
                    }

                    {
                        ((!isMixed && includes(['accepted', 'updated'], _row.status))
                            || (isMixed && canInTransit))
                        &&
                        /*@ts-ignore*/
                        <Tooltip tooltip={ t('in transit') }>
                            <a onClick={ () => onInTransitAlpOutboundLoadGroup(groupToken) }
                               className={ "cursor-pointer ms-1 text-green-500" }>
                                <i className="fas fa-truck-moving" />
                            </a>
                        </Tooltip>
                    }

                    {
                        // On this list we rely on status/counters only; backend will validate all constraints.
                        ((!isMixed
                            && includes(['in-transit'], _row.status)
                            && (!isEmpty(_row.alp_supplier_compound) && !isEmpty(_row.gate_out_confirmed_at)))
                            || (isMixed && canUnload))
                        &&
                        /*@ts-ignore*/
                        <Tooltip tooltip={ t('unload request') }>
                            <a onClick={ () => onUnloadRequestAlpOutboundLoadGroup(groupToken) }
                               className={ "cursor-pointer text-green-500 ms-1" }>
                                <i className="fas fa-truck-loading" />
                            </a>
                        </Tooltip>
                    }

                    <Tooltip tooltip={ t('detail of item') }>
                        <a
                            className={ "cursor-pointer ms-1" }
                            onClick={ () => onOperationClick('detail', groupToken) }>
                            <i className="fas fa-eye" />
                        </a>
                    </Tooltip>
                </HasAccess>
            </>
        );
    };

    const columns = [
        {
            field: 'group_token',
            title: t("group token"),
            sorting: true,
            sortField: "group_token",
            render: (_row: any) => (
                <div>
                    <strong className="cursor-pointer" onClick={ () => onOperationClick('detail', _row.group_token) }>
                        { _row.group_token }
                    </strong>
                    <div className="text-muted small">{ t("qty") }: { _row.qty ?? 0 }</div>
                </div>
            )
        },
        {
            field: 'status',
            title: t("status"),
            sorting: true,
            sortField: "status",
            render: (_row: any) => (
                <AlpOutboundLoadGroupStatusChips row={ _row } t={ t } alwaysShowCounts={ true } />
            ),
        },
        {
            field: 'gate_out_confirmed_at',
            title: t("gate out"),
            sorting: false,
            render: (_row: any) => {
                if ( isEmpty(_row.gate_out_confirmed_at) ) {
                    return <GateOutStatusChip confirmedAt={ null } t={ t } />;
                }

                return (
                    <Tooltip tooltip={ utcDateToLocalDate(_row.gate_out_confirmed_at) }>
                        <GateOutStatusChip
                            confirmedAt={ _row.gate_out_confirmed_at }
                            t={ t }
                            dateLabel={ utcDateToLocalDate(_row.gate_out_confirmed_at) }
                        />
                    </Tooltip>
                );
            }
        },
        {
            field: 'author',
            title: t("author"),
            sorting: false,
            render: (_row: any) => (
                <div className="text-capitalize">
                    { _row?.author?.name || _row?.author?.first_name || "-" }
                </div>
            ),
        },
        {
            field: 'load_numbers',
            title: t("load numbers"),
            sorting: false,
            render: (_row: any) => {
                const numbers = isArray(_row.load_numbers) ? _row.load_numbers : [];
                return (
                    <div className="small">
                        { numbers.length > 0 ? numbers.join(', ') : "-" }
                    </div>
                );
            },
        },
        {
            field: 'created_at',
            title: t("created at"),
            sorting: true,
            sortField: "created_at",
            render: (_row: any) => _row.created_at ? (
                <Tooltip tooltip={ utcDateToLocalDate(_row.created_at) }>
                    { utcDateToLocalDate(_row.created_at) }
                </Tooltip>
            ) : "-",
        },
        {
            field: 'comments',
            title: t('comments'),
            sorting: false,
            align: 'center',
            render: (row: any) => {
                if ( !row?.group_token ) {
                    return null;
                }

                return (
                    <HasAccess hasAnyAccess={ ['loadcommentsview', 'loadcommentsstore'] }>
                        {/*@ts-ignore*/}
                        <Tooltip tooltip={ Number(row?.load_comments_count || 0) > 0 ? `${ t('comments') } (${ row.load_comments_count })` : t('comments') }>
                            <a onClick={ () => onOpenLoadComments(row) }
                               className={ "cursor-pointer ms-1 text-blue-500 load-comment-action" }>
                                <i className="fas fa-comments" />
                                { Number(row?.load_comments_count || 0) > 0 &&
                                  <span className="load-comment-action__count">{ row.load_comments_count }</span>
                                }
                            </a>
                        </Tooltip>
                    </HasAccess>
                );
            },
        },
        {
            field: 'action',
            title: t('action'),
            sorting: false,
            align: 'right',
            render: (_row: any) => (
                <>
                    <AlpOutboundLoadGroupActionButton row={ _row } />
                </>
            )
        },
    ];

    return (
        <div className={ "block-viewer" }>
            <div className="block-section">
                <div className="block-header">
                    <span className="block-title">
                        <h5 className={ "mb-0" }>
                            { t('outbound load groups') } ({ total })
                        </h5>
                    </span>
                    <div className="block-actions">
                        {
                            operation == 'detail' &&
                            <div>
                                <Button type={ "button" }
                                        tooltip={ t("operation cancel") } className={ "ms-2" }
                                        size={ "small" } severity={ "warning" }
                                        tooltipOptions={ { position: 'top' } } onClick={ () => onCloseOperation(false) }>
                                    <i className="pi pi-arrow-left me-2" /> { t("cancel") }
                                </Button>
                            </div>
                        }
                    </div>
                </div>
                <div className="block-content">
                    {
                        operation !== 'detail' &&
                        <Row className={ `p-2` }>
                            <Col sm={ 12 } md={ 6 } className={ "mb-2" }>
                                <Formik
                                    enableReinitialize={ true }
                                    initialValues={ initSearchFormState }
                                    onSubmit={ onSearch }
                                >
                                    { (fp: FormikProps<any>) => (
                                        <Form onSubmit={ fp.handleSubmit }>
                                            <Col sm={ 12 } md={ 8 }>
                                                <div className={'d-flex'}>
                                                    <Field component={ InputField }
                                                           withoutLabel={ true }
                                                           autoFocus={true}
                                                           name="search"
                                                           formGroupClass={ "mb-0" }
                                                           placeholder={ t("search") } />
                                                </div>
                                            </Col>
                                        </Form>
                                    ) }
                                </Formik>
                            </Col>

                            <Col sm={ 12 } md={ 6 } className={ "text-right mb-2" }>
                                <div className="d-inline-block me-2 align-middle text-left">
                                    <QuickCopyDropdown
                                        columns={ columns }
                                        tableName={ "alpAvisoOutboundGroupList" }
                                        selectedRows={ selectedRows }
                                        trackBy={ "id" }
                                        // @ts-ignore
                                        entities={ tableRef.current?.getEntities?.() || [] }
                                    />
                                </div>
                                <ButtonGroup>
                                    <Button onClick={ refreshTable } icon="pi pi-refresh" size={ "small" } outlined
                                            tooltip={ t('refresh') }
                                            tooltipOptions={ { position: 'top' } } />
                                </ButtonGroup>
                            </Col>

                            <Col sm={ 12 }>
                                <StateLessGrid
                                    size={ "normal" }
                                    url={ '/suppliers/provider/aviso/outbound/groups?1=1' }
                                    name={ "alpAvisoOutboundGroupList" }
                                    columns={ columns }
                                    moduleState={ moduleState }
                                    setQuery={ props.setQuery }
                                    ref={ tableRef }
                                    itemSize={ 85 }
                                    setTotal={ setTotal }
                                    stickyHeader={ true }
                                    showQuickCopy={ false }
                                    onRowSelection={ setSelectedRows }
                                    selectedRows={ selectedRows }
                                />
                            </Col>
                        </Row>
                    }

                    {
                        (operation === 'detail' && !isEmpty(operationId)) &&
                        <AlpAvisoOutboundGroupDetailPage
                            toastify={ toastify }
                            groupToken={ operationId }
                            onClose={ onCloseOperation }
                        />
                    }

                    {/* Action panels (same as LoadList for AlpOutboundLoadGroup) */ }
                    <AlpOutboundLoadGroupAccept
                        id={ acceptAlpOutboundLoadGroupId }
                        toastify={ toastify }
                        dropdowns={ dropdowns }
                        onRefresh={ refreshTable }
                        onClose={ () => setAcceptAlpOutboundLoadGroupId(null) }
                    />

                    <AlpOutboundLoadGroupReset
                        id={ resetAlpOutboundLoadGroupId }
                        toastify={ toastify }
                        onRefresh={ refreshTable }
                        onClose={ () => setResetAlpOutboundLoadGroupId(null) }
                    />

                    <AlpOutboundLoadGroupUpdate
                        id={ editAlpOutboundLoadGroupId }
                        toastify={ toastify }
                        dropdowns={ dropdowns }
                        onRefresh={ refreshTable }
                        onClose={ () => setEditAlpOutboundLoadGroupId(null) }
                    />

                    <AlpOutboundLoadGroupInTransit
                        id={ inTransitAlpOutboundLoadGroupId }
                        toastify={ toastify }
                        onRefresh={ refreshTable }
                        onClose={ () => setInTransitAlpOutboundLoadGroupId(null) }
                    />

                    <AlpOutboundLoadGroupUnloadRequest
                        id={ unloadRequestAlpOutboundLoadGroupId }
                        toastify={ toastify }
                        onRefresh={ refreshTable }
                        onClose={ () => setUnloadRequestAlpOutboundLoadGroupId(null) }
                    />

                    <AlpOutboundLoadGroupDetach
                        id={ detachAlpOutboundLoadGroupId }
                        toastify={ toastify }
                        onRefresh={ refreshTable }
                        onClose={ () => setDetachAlpOutboundLoadGroupId(null) }
                    />

                    <LoadComments
                        id={ commentLoad ? (commentLoad.load_source_id || commentLoad.source_id || commentLoad.id) : null }
                        load={ commentLoad }
                        toastify={ toastify }
                        onRefresh={ refreshTable }
                        onClose={ () => {
                            setCommentLoad(null);
                            focusOnSearch();
                        } } />
                </div>
            </div>
            <ConfirmPopup />
        </div>
    );
};

export default withTranslation()(WithRouter(AlpAvisoOutboundGroupList));

